import { Box, GlobalStyles, ThemeProvider } from "@mui/material";
import { useSignal, useSignalEffect } from "@preact/signals-react";
import { isNil } from "lodash-es";
import { useEffect } from "react";
import {
  LoaderFunctionArgs,
  RouteObject,
  createBrowserRouter,
  redirectDocument,
  useLoaderData,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Layout, Menu, Page, PageTemplate } from "./../resolvers-types";
import { NGAutoLogoutCountdown } from "./components/NGAutoLogoutCountdown/NGAutoLogoutCountdown";
import NGOverlay from "./components/NGOverlay/NGOverlay";
import NGPageTemplate from "./components/NGPageTemplate/NGPageTemplate";
import { reviewServices } from "./components/NGReviewDialog/NGReviewDialogPopups.ts";
import NGDesignOverlay from "./generators/NGDesignOverlay";
import ErrorElement from "./generators/NGErrorElement.tsx";
import NGInspector from "./generators/NGInspector.tsx";
import NGPage from "./generators/NGPage";
import useCookieConsentBanner from "./hooks/useCookieConsentBanner.tsx";
import { publishPageViewOnLoad } from "./library/analytics.ts";
import { setupAuth } from "./library/auth";
import { setupReferencedComponentsAndServicesInpage } from "./library/components";
import {
  ClearSite,
  GetDynamicPage,
  GetSiteFromServer,
  GetStaticPage,
  NGNextPage,
  clearState,
  getDefaultContext,
  getState,
  initStateFromUrl,
  setImpersonation,
  setupHandlers,
  setupLocalState,
  setupPage,
} from "./library/dataService";
import { designerState, setupDesigner } from "./library/designer";
import { LogTag, log } from "./library/logger";
import { isNative } from "./library/native";
import { client } from "./library/nats-client";
import { GetReleaseInfoState } from "./library/releaseInfo.ts";
import {
  getNextImpersonationTabId,
  isExternalUrl,
  isNullOrEmpty,
  setCookie,
  setupFavicon,
  toRelativeUrl,
} from "./library/utils";
import { fontImportMap } from "./loaders/fontLoader";
import { Result } from "./models/result";
import { impersonateUserOverlay as impersonatingOverlay } from "./sampleData/impersonatingUserOverlay.ts";
import serviceNotesHistory from "./sampleData/review-history/service-review-notes-history.ts";
import { anonymousSiteConfig } from "./sampleData/siteSample";
import { themes } from "./theme";

const tag: LogTag = "NGRouter";

type ParentState = {
  Authenticated: boolean;
  Menu: Menu;
  Template: PageTemplate;
  Site: any;
  SessionId?: string;
  authError?: boolean;
};

const ParentRoute = () => {
  const config = useLoaderData() as ParentState;

  const navigate = useNavigate();
  const location = useLocation();

  const context = getDefaultContext();
  const { global } = getState(context);
  const menu = global.Menu.value;
  const site = global.NGSite.value;

  useSignalEffect(() => {
    if (isNil(NGNextPage.value)) return;

    if (isExternalUrl(NGNextPage.value)) {
      window.location.href = NGNextPage.value;
      return;
    } else {
      navigate(toRelativeUrl(NGNextPage.value));
    }

    NGNextPage.value = null;
  });

  useEffect(() => {
    if (isNil(menu?.LandingPageUrl)) return;

    if (location.pathname === "/") {
      navigate(menu?.LandingPageUrl);
    }
    // redirect impersonated user to their landing page
    if (!isNullOrEmpty(config.SessionId) && location.pathname === `/u/${config.SessionId}/`) {
      const landingPage = menu?.LandingPageUrl.startsWith("/")
        ? menu?.LandingPageUrl.substring(1)
        : menu?.LandingPageUrl;
      navigate(`/u/${config.SessionId}/${landingPage}`);
    }
  }, [config.SessionId, location.pathname, menu, navigate]);

  useEffect(() => {
    initStateFromUrl(new URLSearchParams(location.search));
  }, [location.search]);

  useEffect(() => {
    setupFavicon(site?.Theme);
  }, [site]);

  const CookieConsent = useCookieConsentBanner(context);
  const currentTheme = themes["default"];

  const modulesLoaded = useSignal(false);

  useEffect(() => {
    const controller = new AbortController();
    const fonts = site?.Theme?.FontFamily?.length
      ? site.Theme.FontFamily
      : [
          { Value: "roboto300" },
          { Value: "roboto400" },
          { Value: "roboto500" },
          { Value: "roboto700" },
          { Value: "inter400" },
        ];

    async function loadFonts() {
      const loadPromises = fonts.map(async (font) => {
        if (controller.signal.aborted) return;

        const key = font?.Value?.toLowerCase();
        const loader = fontImportMap[key];

        if (!loader) {
          log.warn(tag, `Font "${font?.Value}" not found in fontImportMap`);
          return;
        }

        try {
          await loader();
          if (!controller.signal.aborted) {
            log.info(tag, `Font ${font?.Value} loaded`);
          }
        } catch (err) {
          if (!controller.signal.aborted) {
            log.error(tag, `Error loading font ${font?.Value}`, err);
          }
        }
      });

      await Promise.all(loadPromises);

      if (!controller.signal.aborted) {
        modulesLoaded.value = true;
      }
    }

    loadFonts();

    return () => controller.abort();
  }, [site]);

  return (
    global.NGTemplate.value &&
    modulesLoaded.value && (
      <>
        <ThemeProvider theme={currentTheme}>
          {site && (
            <>
              <GlobalStyles styles={site.Theme?.Styles} />
              <GlobalStyles styles={site.ParentTheme?.Styles} />
            </>
          )}
          <NGAutoLogoutCountdown />
          <NGOverlay key="impersonating-overlay" config={impersonatingOverlay} context={context} />
          <NGInspector config={{ Id: "inspector-overlay" }} context={context} />
          {config.authError && (
            <Box
              sx={{
                marginTop: "2rem",
                display: "flex",
                justifyContent: "center",
                flexDirection: "row",
                width: "100%",
                border: "solid 1px #aaa",
                height: "50px",
                alignItems: "center",
                borderRadius: "8px",
                fontFamily: "Inter",
              }}
            >
              We could not authenticate you. Please refresh the page and try again in a minute.
            </Box>
          )}
          {!config.authError && <NGPageTemplate config={global.NGTemplate.value} context={context} />}
          <CookieConsent />
        </ThemeProvider>
      </>
    )
  );
};

// Export this loader function to use in your route definition
export async function forceUpgradeLoader() {
  return await GetStaticPage("force-upgrade");
}

const ForceUpgradeRoute = () => {
  const pageMetadata = useLoaderData() as Page;
  const context = getDefaultContext();

  // Optional: Add a fallback for loading states
  if (!pageMetadata) return <div>Loading...</div>;

  return <NGPage config={pageMetadata} layout={pageMetadata.Layout as Layout} context={context} />;
};

// Export this loader function to use in your route definition
export async function maintenanceModeLoader() {
  return await GetStaticPage("maintenance");
}

const MaintenanceModeRoute = () => {
  const pageMetadata = useLoaderData() as Page;
  const context = getDefaultContext();

  // Optional: Add a fallback for loading states
  if (!pageMetadata) return <div>Loading...</div>;

  return <NGPage config={pageMetadata} layout={pageMetadata.Layout as Layout} context={context} />;
};

const ChildRoute = () => {
  const pageMetadata: Page = useLoaderData() as Page; // Use the hook to get the pre-loaded data
  const context = getDefaultContext();

  // Page timer and page_view audit are now handled in route loader

  if (isNil(pageMetadata)) return <div />;

  return <NGPage config={pageMetadata} layout={pageMetadata.Layout as Layout} context={context} />;
};

const EditorRoute = () => {
  const config = useLoaderData() as any; // Use the hook to get the pre-loaded data

  const context = getDefaultContext();

  context.InDesignMode = true;
  const site = useSignal(config.site);

  const local = setupLocalState(
    {
      Bindings: {
        Page: "State.Page",
        UndoRedoPageStackIndex: "State.UndoRedoPageStackIndex",
        // ShowInvisibleComponents: "State.EditorShowInvisibleComponents",
      },
    },
    {
      Page: useSignal(null),
      UndoRedoPageStackIndex: useSignal(0),
    },
    context
  );

  function handleMessage(message) {
    const msg = message.data;
    if (msg.Source !== "ng") return;

    const handlers = setupHandlers(msg, context);

    const { global } = getState(context);
    log.debug(tag, "Editor frame handle message", msg, global);

    if (msg.Type == "stylesUpdated") {
      ClearSite();
      GetSiteFromServer(context).then((site2) => {
        site.value = site2;
      });
    } else if (msg.Type == "selectItems") {
      designerState.Selected.value = msg.Selected;
    } else if (msg.Type == "toggleInvisibleComponents") {
      designerState.ShowInvisibleComponents.value = msg.Value;
    } else if (msg.Type == "toggleIgnoreSampleDataInEditor") {
      designerState.IgnoreSampleDataInEditor.value = msg.Value;
      local.Page.value = { ...local.Page.value };
    } else if (msg.Type == "setInteractionMode") {
      designerState.InteractionMode.value = msg.Value;
    } else if (msg.Type == "UpdatePage") {
      local.Page.value = { ...local.Page.value };
    }

    if (handlers[msg.Type]) {
      handlers[msg.Type](msg, msg);
    }
  }

  useSignalEffect(() => {
    setupDesigner(local.Page.value);
  });

  useEffect(() => {
    window.addEventListener("message", handleMessage);

    window.parent.postMessage(
      {
        Source: "ng",
        Type: "onInit",
        Data: null,
      },
      "*",
      []
    );

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  return (
    <Box
      className="designer"
      sx={{
        position: "relative",
        width: "100%",
        height: "100vh",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <GlobalStyles styles={site.value?.Theme?.Styles} />
      <GlobalStyles styles={site.value?.ParentTheme?.Styles} />
      {local.Page.value && (
        <>
          <NGPage config={local.Page.value} layout={local.Page.value?.Layout} context={context} />
          <NGDesignOverlay config={local.Page.value} context={context} />
        </>
      )}
    </Box>
  );
};

function ImpersonateErrorElement() {
  return (
    <div>
      <h2>Impersonation failed</h2>
    </div>
  );
}

const fallbackTemplate = {
  __typename: "PageTemplate",
  Id: "0xf021",
  UniqueName: "Fallback Template",
  Description: "Fallback Template",
  TopAppBar: {
    Id: "0xf022",
    UniqueName: "Top Navigation",
  },
};

const localLoader = async (args: LoaderFunctionArgs) => {
  const { request, params } = args;

  // Setup auth before loading anything to avoid a race
  const context = getDefaultContext();
  const { global } = getState(context);
  const auth = await setupAuth(args, global, context);
  if (!auth?.isUserLoggedIn) {
    if (isNative() && params.routeId != "login") {
      return null;
    }
  }

  if (params.routeId == "login" && !isNative()) {
    return redirectDocument(`/`);
  }

  // Get the page for the feature
  const foundPage = await GetStaticPage(params.routeId);
  await setupReferencedComponentsAndServicesInpage(foundPage);
  publishPageViewOnLoad(foundPage, context);

  clearState(request.url);
  setupPage(foundPage);

  return foundPage;
};

const interpretedPageLoader = async (args: LoaderFunctionArgs) => {
  const { request, params } = args;

  // Setup auth before loading anything to avoid a race
  const context = getDefaultContext();
  const { global } = getState(context);
  const auth = await setupAuth(args, global, context);
  if (!auth?.isUserLoggedIn) {
    return null;
  }

  // Check if setupAuth already fetched the page (for public token flow)
  let page = (auth as any)?.page || null;

  // If page wasn't fetched by setupAuth, fetch it now
  if (!page) {
    page = await GetDynamicPage(params.routeId, context);
  }

  await setupReferencedComponentsAndServicesInpage(page);

  clearState(request.url);
  if (page) {
    publishPageViewOnLoad(page, context);

    // add reviews services to the page
    page.Services = (page.Services ?? []).concat([...reviewServices, serviceNotesHistory]);
    setupPage(page);

    return page;
  } else {
    return null;
  }
};

const editorLoader = async (args: LoaderFunctionArgs) => {
  const { request, params } = args;
  const context = getDefaultContext();
  const { global } = getState(context);
  const auth = await setupAuth(args, global, context);
  if (!auth?.isUserLoggedIn) {
    return null;
  }

  clearState(request.url); // removes contamination from previous page state
  const site = await GetSiteFromServer(context);

  return {
    mode: params.mode,
    site: site,
  };
};

const rootPathLoader = async (args) => {
  const { request, params } = args;

  const config = {
    Authenticated: false,
    Menu: {} as Menu | null,
    Template: anonymousSiteConfig,
    Site: null,
    SessionId: null,
    authError: false,
  };

  if (!isNil(params.sessionId)) {
    const userId = localStorage.getItem(`Api.Imp.${params.sessionId}.UserId`);

    if (isNullOrEmpty(userId)) {
      return redirectDocument(`/`);
    } else {
      config.SessionId = params.sessionId;
    }
  }

  const context = getDefaultContext();
  const { global } = getState(context);

  const auth = await setupAuth(args, global, context);

  // if Keycloak return error redirect to login
  if (!isNil((auth as any)?.initializationError)) {
    // return redirectDocument(`/`);
    config.authError = true;
    return config;
  }

  if (!auth?.isUserLoggedIn) {
    if (isNative()) {
      const url = new URL(request.url);
      if (url.pathname != "/login") {
        // TODO - what do we do in the scenario where there is a user session ??
        // && !basePartRegex.test(url.pathname)
        return redirectDocument(`/login?Redirect=${url.pathname}${url.search}`);
      }
    }

    return config;
  }
  config.Authenticated = true;
  GetReleaseInfoState(context);
  return config;
};

const routes = [
  {
    path: "/force-upgrade",
    element: <ForceUpgradeRoute />,
    loader: forceUpgradeLoader,
  },
  {
    path: "/maintenance",
    element: <MaintenanceModeRoute />,
    loader: maintenanceModeLoader,
  },
  {
    path: "/impersonate",
    loader: async ({ request }) => {
      const n = getNextImpersonationTabId();

      const tabId = n.toString();

      const impersonateId = new URL(request.url).searchParams.get("ImpersonationRequestId");
      if (!isNil(impersonateId)) {
        const resp = (await client.request("impersonationrequest.get", { Id: impersonateId })) as Result<any>;
        if (!resp?.success) {
          log.error(tag, "Service impersonationrequest.get failed", resp?.reasons);
          throw new Error("Impersonation request not found");
        }

        if (typeof localStorage != "undefined") {
          // sessionStorage.setItem("impersonateId", impersonateId);
          localStorage.setItem(`Api.Imp.${tabId}.NatsJwt`, resp?.data.NatsJwt);
          localStorage.setItem(`Api.Imp.${tabId}.NatsSeed`, resp?.data.NatsSeed);
          localStorage.setItem(`Api.Imp.${tabId}.RefreshToken`, resp?.data.RefreshToken);
          localStorage.setItem(`Api.Imp.${tabId}.UserId`, resp?.data.UserId);
        }

        setCookie(`Api.Imp.${tabId}.Token`, resp?.data.Token, 1);

        setImpersonation(resp?.data.UserId, tabId);

        // force NATS to reconnect with the new credentials
        client.disconnect();
      }

      return redirectDocument(`/u/${tabId}/`);
    },
    element: <div>test</div>,
    errorElement: <ImpersonateErrorElement />,
  },
  {
    path: "/u/:sessionId/",
    element: <ParentRoute />,
    loader: rootPathLoader,
    errorElement: <ErrorElement />,
    children: [
      {
        path: "/u/:sessionId/local/:routeId", //, "/u/1/local/:routeId"], // For static routes; this will disappear in the future
        loader: localLoader,
        element: <ChildRoute />,
      },
      {
        path: "/u/:sessionId/:routeId", // ["/:routeId", "/u/*/:routeId"], // For dynamic routes; this will disappear in the future
        loader: interpretedPageLoader,
        element: <ChildRoute />,
      },
    ] as RouteObject[],
  },
  {
    path: "/",
    element: <ParentRoute />,
    loader: rootPathLoader,
    errorElement: <ErrorElement />,
    children: [
      {
        path: "/local/:routeId", //, "/u/1/local/:routeId"], // For static routes; this will disappear in the future
        loader: localLoader,
        element: <ChildRoute />,
      },
      {
        path: "/:routeId", // ["/:routeId", "/u/*/:routeId"], // For dynamic routes; this will disappear in the future
        loader: interpretedPageLoader,
        element: <ChildRoute />,
      },
    ] as RouteObject[],
  },
  {
    path: "/ngeditor/:mode",
    loader: editorLoader,
    element: <EditorRoute />,
  },
  {
    path: "/u/:sessionId/ngeditor/:mode",
    loader: editorLoader,
    element: <EditorRoute />,
  },
];

export const NGRouter = createBrowserRouter(routes);
